import React, { useEffect, useState } from 'react';

import auth from 'app/core/auth';
import { useAuthSession, useAuthUserContext } from 'app/core/security/authSession';
import { requestSessionLogout } from 'app/core/security/sessionIdle';
import { createInboxNotificationsConnection } from 'app/modules/FormDefinition/services/hubConnection';
import { useCreateConnection } from 'app/modules/FormDefinition/services/signalr.hooks';
import Avatar from 'react-avatar';
import { Dropdown } from 'react-bootstrap';
import {
  BoxArrowRight,
  CaretDown,
  Envelope,
  House,
  List,
  Person,
  PersonWorkspace
} from 'react-bootstrap-icons';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { counNoReadInbox } from '../modules/Inbox/Notifications/services/inbox.service';

import { useLayoutActions } from './store';

type CustomToggleProps = React.PropsWithChildren<{
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
}>;

const CustomToggle = React.forwardRef<HTMLAnchorElement, CustomToggleProps>(
  ({ children, onClick }, ref) => (
    <a
      href="#"
      ref={ref}
      className="nav-link d-flex align-items-center gap-1"
      onClick={(e) => {
        e.preventDefault();
        onClick?.(e);
      }}
    >
      <Person />
      <span>{children}</span>
      <CaretDown size={14} />
    </a>
  )
);

CustomToggle.displayName = 'CustomToggle';

const NavBar = () => {
  const session = useAuthSession();
  const navigate = useNavigate();
  const { openMenu } = useLayoutActions();
  const user = useAuthUserContext();
  const { t } = useTranslation();

  const [inbox, setInbox] = useState(0);
  const { connection } = useCreateConnection(createInboxNotificationsConnection, false);

  async function fetchNoReadInbox() {
    const result: any = await counNoReadInbox();
    setInbox(result);
  }

  useEffect(() => {
    fetchNoReadInbox();
  }, []);

  useEffect(() => {
    if (!connection) return;

    connection.on('inbox', fetchNoReadInbox);

    return () => {
      connection.off('inbox', fetchNoReadInbox);
    };
  }, [connection]);

  return (
    <nav className="navbar navbar-expand-sm navbar-dark bg-dark fixed-top py-2">
      <div className="container-fluid">
        <button
          type="button"
          className="navbar-brand btn btn-link text-decoration-none text-white d-flex align-items-center mb-0 p-0"
          onClick={openMenu}
        >
          <List size={22} />
          <span className="ms-2">Menu</span>
        </button>

        <ul className="navbar-nav ms-auto flex-row align-items-center gap-2">
          <li className="nav-item">
            <button
              type="button"
              className="nav-link btn btn-link text-decoration-none"
              onClick={() => navigate('/')}
            >
              <House size={20} />
            </button>
          </li>

          <li className="nav-item">
            <button
              type="button"
              className="nav-link btn btn-link text-decoration-none position-relative"
              onClick={() => navigate('/inbox/notifications')}
            >
              <Envelope size={20} />

              {inbox > 0 && (
                <span className="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger">
                  {inbox}
                </span>
              )}
            </button>
          </li>

          {!session.user && (
            <li className="nav-item">
              <button
                type="button"
                className="nav-link btn btn-link text-decoration-none"
                onClick={() => auth.login()}
              >
                Login
              </button>
            </li>
          )}

          {session.user && (
            <li className="nav-item">
              <Dropdown align="end">
                <Dropdown.Toggle as={CustomToggle} id="user-menu">
                  {user?.userProfile?.username}
                </Dropdown.Toggle>

                <Dropdown.Menu>
                  <Dropdown.ItemText>
                    <div className="d-flex align-items-center">
                      <Avatar name={user?.userProfile?.username} round size="60px" />

                      <div className="ms-3">
                        <small className="text-muted">{t('common.label.current-user')}</small>
                      </div>
                    </div>

                    <div className="mt-2">{user?.userProfile?.username}</div>

                    <div className="fw-bold">{user?.userProfile?.name}</div>
                  </Dropdown.ItemText>

                  <Dropdown.Divider />

                  <Dropdown.Item onClick={() => navigate('/administration/user-profile')}>
                    <PersonWorkspace className="text-muted mb-1 me-2" />
                    {t('common.label.user-profile')}
                  </Dropdown.Item>

                  <Dropdown.Item onClick={() => requestSessionLogout('manual')}>
                    <BoxArrowRight className="text-muted mb-1 me-2" />
                    {t('common.label.logout')}
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </li>
          )}
        </ul>
      </div>
    </nav>
  );
};

export default React.memo(NavBar);
